<script lang="ts" setup>
  import { type PropType, computed, ref } from 'vue';
  import type { Photo } from '@storefront/resources';
  import type { Breadcrumb, Section } from '@storefront/types';

  type Category = {
    title: string | null
  }

  type Author = {
    name: string | null
  }

  type Post = {
    title: string | null;
    descriptionShort: string | null;
    date: string | null;
    category: Category | null;
    author: Author | null;
    photos: Photo[];
  }

  const props = defineProps({
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: false, default: [] },
    sections: { type: Array as PropType<Section[]>, required: false, default: [] },
    recommended: { type: Array, required: false, default: [] },
    post: { type: Object as PropType<Post>, required: true },
  });

  const alt = computed(() => {
    return props.post?.photos[0]?.alt || props.post.title;
  });

  const parameters = ref({
    navigation: true,
    breakpoints: {
      1: {
        slidesOffsetBefore: 10,
        slidesPerView: 1.5,
      },
      567: {
        slidesOffsetBefore: 10,
        slidesPerView: 2,
      },
      992: {
        slidesOffsetBefore: 0,
        slidesPerView: 3,
      },
    },
  });
</script>
<template>
  <main id="main" class="container blog-article">
    <article class="blog-article__article">
      <div class="blog-article__title">
        <SfBreadcrumbs :breadcrumbs="breadcrumbs" />
        <header class="blog-article__title-container">
          <div class="blog-article__photo">
            <MediaPicture :alt :photo="post.photos[0]" sizes="1080x800 2x, 540x400" type="banner" />
          </div>
          <div class="blog-article__header">
            <SfHeading class="blog-article__heading" larger type="h1">
              {{ post.title }}
            </SfHeading>
            <div class="blog-article__description" v-html="post.descriptionShort" />
            <div class="blog-article__meta-data">
              <RouterLink v-if="post.category" :href="$route('web.blog.category.show', { category: post.category })" class="blog-article__meta-data-item">
                <SfIconBookmark aria-hidden="true" />
                {{ post.category.title }}
              </RouterLink>
              <span v-if="post.date" class="blog-article__meta-data-item">
                <SfIconClock aria-hidden="true" />
                {{ post.date }}
                <!--                @TODO: SFDATETIME-->
                <!--                <SfDateTime :value="post.date" />-->
              </span>
              <span v-if="post.author?.name" class="blog-article__meta-data-item">
                <SfIconClock aria-hidden="true" />
                {{ post.author.name }}
              </span>
            </div>
          </div>
        </header>
      </div>
      <SectionRenderer :sections="sections" />
      <SfSection v-if="recommended.length > 0" :title="$t('SHOP.BLOG.LABEL.READ_ALSO')" class="blog-article__recommended">
        <SfArticlesCarousel :parameters :posts="recommended" />
      </SfSection>
    </article>
  </main>

  <RouterView />
</template>
<style lang="scss" scoped>
  .blog-article {
    --sf-breadcrumbs-padding-y: 0;
    --sf-breadcrumbs-padding-y-lg: 0;

    &__article,
    &__header {
      display: flex;
      flex-direction: column;
    }

    &__title-container {
      display: grid;
      grid-template-columns: 300px 1fr;
      gap: 1rem;
    }

    &__article {
      max-width: 1100px;
      margin: 2rem auto;
      gap: 2rem;
    }

    &__description {
      font-size: 0.875rem;
    }

    &__meta-data {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem 1rem;
    }

    &__header {
      gap: 0.5rem;
    }

    &__meta-data-item {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.875rem;
    }

    &__title {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__photo :deep(img) {
      aspect-ratio: 540/400;
      border-radius: var(--border-radius);
    }

    @media (max-width: 992px) {
      .blog-article {
        &__article {
          margin: 1rem auto;
          gap: 1rem;
        }

        &__recommended {
          padding: 1rem 0;
        }

        &__meta-data-item {
          font-size: 0.75rem;
        }

        &__title-container {
          padding: 0 0.625rem;
        }
      }
    }

    @media (max-width: 576px) {

      &__title-container {
        grid-template-columns: 1fr;
        flex-direction: column;
      }
    }
  }
</style>
